import React, { useRef, useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Footer } from './shared/ServicePageSections';

/* ─── helpers ─── */
function useRevealOnce(threshold = 0.1) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVisible(true); obs.disconnect(); }
    }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

function useCount(target, active, duration = 1600) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active) return;
    let start = null;
    const tick = ts => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      setVal(Math.round((1 - Math.pow(1 - p, 3)) * target));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [active, target, duration]);
  return val;
}

/* ─── sector data ─── */
const SECTORS = [
  {
    num: '01', name: 'Healthcare', accent: '& Medical Devices',
    color: '#C0392B', to: '/industries/healthcare',
    tag: 'Life-Critical IoT',
    headline: 'Precision Connected Health',
    desc: 'IoT solutions for healthcare environments where failure is never an option — combining reliability, low-latency alerting, and security compliance that clinical use demands. From NICU monitors to connected medical devices.',
    cases: ['Neonatal Care Solutions', 'Phototherapy Treatment Systems', 'Remote Patient Monitoring (RPM)', 'Nutrition Assessment & Digital Health Platforms', 'Connected Medical Devices (Medical IoT)'],
    stats: [{ v: '30%', l: 'Fewer Readmissions' }, { v: '45%', l: 'Faster Response' }, { v: '$14.9B', l: 'Market by 2028' }],
  },
  {
    num: '02', name: 'Utilities', accent: '& Energy',
    color: '#E67E22', to: '/industries/utilities-energy',
    tag: 'Smart Grid & Metering',
    headline: 'Intelligent Energy Infrastructure',
    desc: 'Smart metering, real-time grid monitoring, and demand response automation that reduce losses, improve reliability, and bring energy infrastructure into the data age — at every scale.',
    cases: ['Energy Monitoring & Analytics', 'Battery & Energy Storage Monitoring', 'DG Automation & Remote Control', 'Renewable Energy & Solar Monitoring', 'Industrial Electrical Asset Monitoring', 'Predictive Maintenance & Industrial IoT'],
    stats: [{ v: '25%', l: 'Energy Savings' }, { v: '60%', l: 'Less Manual Work' }, { v: '3x', l: 'Faster Fault Detection' }],
  },
  {
    num: '03', name: 'Industrial', accent: 'IoT & Manufacturing',
    color: '#5D4037', to: '/industries/industrial-iot',
    tag: 'Industry 4.0',
    headline: 'Smart Manufacturing & Predictive Operations',
    desc: 'Machine health monitoring, predictive maintenance, OEE tracking, and process automation that reduce unplanned downtime, improve quality, and bring Industry 4.0 to the factory floor — practically and affordably.',
    cases: ['Smart Factory & Machine Monitoring', 'Predictive Maintenance Solutions', 'Industrial Data Acquisition & Dashboards', 'Connected Industrial IoT Platforms', 'Industrial Asset & Utility Monitoring', 'Industrial Automation & Control Systems'],
    stats: [{ v: '45%', l: 'Less Downtime' }, { v: '20%', l: 'Better OEE' }, { v: '4mo', l: 'Avg ROI Time' }],
  },
  {
    num: '04', name: 'Education', accent: '& EdTech',
    color: '#1565A7', to: '/industries/education',
    tag: 'STEM & Smart Learning',
    headline: 'Hands-On STEM, Cloud-Connected Learning',
    desc: 'STEM and IoT learning kits, robotics labs, and industry-ready engineering labs, backed by cloud-based learning platforms that make classrooms genuinely hands-on and data-driven.',
    cases: ['STEM & IoT Learning Kits', 'Robotics & Automation Labs', 'Engineering Lab Development', 'Cloud-Based Learning Platforms'],
    stats: [{ v: '$3.5B', l: 'EdTech Market by 2028' }, { v: '40%', l: 'Better Concept Retention' }, { v: '3x', l: 'Faster Skills Readiness' }],
  },
];

const HERO_STATS = [
  { target: 4,   suffix: '',   label: 'Industries Covered' },
  { target: 120, suffix: '+',  label: 'Products Shipped' },
  { target: 8,   suffix: '+',  label: 'Years in IoT' },
  { target: 45,  suffix: '',   label: 'Engineers' },
];

/* ─── sector icons ─── */
function SectorIcon({ name, color, size = 52 }) {
  const f = `${color}28`;
  switch (name) {
    case 'Healthcare':
      return (
        <svg width={size} height={size} viewBox="0 0 52 52" fill="none">
          <rect x="22" y="6" width="8" height="40" rx="4" fill={f}/>
          <rect x="6" y="22" width="40" height="8" rx="4" fill={f}/>
          <rect x="24" y="8" width="4" height="36" rx="2" fill={color}/>
          <rect x="8" y="24" width="36" height="4" rx="2" fill={color}/>
        </svg>
      );
    case 'Utilities':
      return (
        <svg width={size} height={size} viewBox="0 0 52 52" fill="none">
          <path d="M30 4L12 30H26L20 48L40 22H26L30 4Z" fill={f} stroke={color} strokeWidth="1.5" strokeLinejoin="round"/>
          <path d="M30 6L15 29H27L22 46L38 24H26L30 6Z" fill={color} opacity="0.55"/>
        </svg>
      );
    case 'Industrial':
      return (
        <svg width={size} height={size} viewBox="0 0 52 52" fill="none">
          <circle cx="26" cy="26" r="12" fill={f} stroke={color} strokeWidth="1.5"/>
          <circle cx="26" cy="26" r="5" fill={color} opacity="0.5"/>
          {[0, 60, 120, 180, 240, 300].map((a, i) => {
            const r = a * Math.PI / 180;
            return <line key={i} x1={26 + 12 * Math.cos(r)} y1={26 + 12 * Math.sin(r)} x2={26 + 19 * Math.cos(r)} y2={26 + 19 * Math.sin(r)} stroke={color} strokeWidth="3.5" strokeLinecap="round"/>;
          })}
        </svg>
      );
    case 'Education':
      return (
        <svg width={size} height={size} viewBox="0 0 52 52" fill="none">
          <path d="M26 8L47 19L26 30L5 19L26 8Z" fill={f} stroke={color} strokeWidth="1.5" strokeLinejoin="round"/>
          <path d="M14 24V36C14 36 18 44 26 44C34 44 38 36 38 36V24" stroke={color} strokeWidth="1.5" strokeLinecap="round" fill="none"/>
          <line x1="47" y1="19" x2="47" y2="32" stroke={color} strokeWidth="1.8" strokeLinecap="round"/>
          <circle cx="47" cy="34" r="2.5" fill={color}/>
        </svg>
      );
    default: return null;
  }
}

/* ══════════════════════════════════════════
   SECTOR ORBIT ICONS  (small, for orbit nodes)
══════════════════════════════════════════ */
function SectorOrbitIcon({ name, color, size = 22 }) {
  if (name === 'Healthcare')
    return <svg width={size} height={size} viewBox="0 0 24 24" fill="none"><path d="M12 4v16M4 12h16" stroke={color} strokeWidth="2.2" strokeLinecap="round"/></svg>;
  if (name === 'Utilities')
    return <svg width={size} height={size} viewBox="0 0 24 24" fill="none"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke={color} strokeWidth="1.8" strokeLinejoin="round"/></svg>;
  if (name === 'Industrial')
    return <svg width={size} height={size} viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="3" stroke={color} strokeWidth="1.7"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3M5.64 5.64l2 2M16.36 16.36l2 2M5.64 18.36l2-2M16.36 7.64l2-2" stroke={color} strokeWidth="1.5" strokeLinecap="round"/></svg>;
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none"><path d="M12 3L2 8l10 5 10-5-10-5z" stroke={color} strokeWidth="1.8" strokeLinejoin="round"/><path d="M6 10.6V16c0 1.66 2.69 3 6 3s6-1.34 6-3v-5.4" stroke={color} strokeWidth="1.8" strokeLinecap="round"/></svg>;
}

/* ══════════════════════════════════════════
   INDUSTRIES ORBIT VISUALIZATION
══════════════════════════════════════════ */
const ORBIT_SECTORS = [
  { name: 'Healthcare', short: 'HEALTH',     color: '#C0392B' },
  { name: 'Utilities',  short: 'UTILITIES',  color: '#E67E22' },
  { name: 'Industrial', short: 'INDUSTRIAL', color: '#5D4037' },
  { name: 'Education',  short: 'EDUCATION',  color: '#1565A7' },
];

function IndustriesOrbit() {
  const SIZE = 460;
  const DUR = 38;

  return (
    <div style={{ position: 'relative', width: SIZE, height: SIZE, flexShrink: 0 }}>
      {/* Ambient glow */}
      <div style={{ position: 'absolute', inset: -32, background: 'radial-gradient(ellipse at 50% 50%, rgba(255,0,4,0.045) 0%, rgba(0,60,255,0.03) 45%, transparent 70%)', borderRadius: '50%', pointerEvents: 'none' }}/>

      {/* Decorative rings: outer halo, orbit track, mid, inner */}
      {[178, 168, 128, 82].map((r, i) => (
        <div key={i} style={{
          position: 'absolute', left: '50%', top: '50%',
          width: r * 2, height: r * 2, margin: -r,
          borderRadius: '50%',
          border: `1px ${i === 1 ? 'solid' : 'dashed'} rgba(10,10,20,${i === 0 ? '0.04' : i === 1 ? '0.11' : i === 2 ? '0.07' : '0.05'})`,
          pointerEvents: 'none',
        }}/>
      ))}

      {/* Center IoT hub */}
      <div style={{
        position: 'absolute', left: '50%', top: '50%',
        width: 84, height: 84, margin: -42,
        background: 'rgba(255,255,255,0.97)',
        border: '1.5px solid rgba(10,10,20,0.10)',
        borderRadius: 22,
        boxShadow: '0 6px 32px rgba(10,10,20,0.11), 0 0 0 5px rgba(255,255,255,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 10,
      }}>
        <svg width="38" height="38" viewBox="0 0 38 38" fill="none">
          <circle cx="19" cy="19" r="5.5" stroke="var(--ink)" strokeWidth="1.5" fill="rgba(10,10,20,0.07)"/>
          <path d="M19 4v6M19 28v6M4 19h6M28 19h6" stroke="var(--ink)" strokeWidth="1.6" strokeLinecap="round" opacity="0.75"/>
          <path d="M9.9 9.9l3.8 3.8M24.3 24.3l3.8 3.8M9.9 28.1l3.8-3.8M24.3 13.7l3.8-3.8" stroke="var(--ink)" strokeWidth="1.2" strokeLinecap="round" opacity="0.28"/>
          <circle cx="19" cy="19" r="10" stroke="var(--ink)" strokeOpacity="0.07" strokeWidth="1" fill="none"/>
        </svg>
      </div>

      {/* Inner satellite dots — orbit at r=105, faster */}
      {[0, 1, 2, 3].map(i => (
        <div key={`sd-${i}`} style={{
          position: 'absolute', left: '50%', top: '50%',
          width: 0, height: 0,
          animation: `sectOrbitFast 15s linear infinite`,
          animationDelay: `${-(15 / 4) * i}s`,
          willChange: 'transform',
        }}>
          <div style={{
            position: 'absolute', transform: 'translate(-4px, -4px)',
            width: 8, height: 8, borderRadius: '50%',
            background: 'rgba(10,10,20,0.14)',
          }}/>
        </div>
      ))}

      {/* Orbiting sector nodes — r=168, slow */}
      {ORBIT_SECTORS.map((s, i) => (
        <div key={i} style={{
          position: 'absolute', left: '50%', top: '50%',
          width: 0, height: 0,
          animation: `sectOrbit ${DUR}s linear infinite`,
          animationDelay: `${-(DUR / ORBIT_SECTORS.length) * i}s`,
          willChange: 'transform',
        }}>
          <div style={{
            position: 'absolute', transform: 'translate(-35px, -35px)',
            width: 70, height: 70,
            background: `${s.color}0D`,
            border: `1.5px solid ${s.color}38`,
            borderRadius: 18,
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 5,
            boxShadow: `0 4px 20px ${s.color}18`,
            backdropFilter: 'blur(4px)',
          }}>
            <SectorOrbitIcon name={s.name} color={s.color} size={22}/>
            <span style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 6, fontWeight: 700, letterSpacing: 0.4,
              textTransform: 'uppercase', color: s.color, lineHeight: 1,
            }}>{s.short}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════
   HERO STAT — count-up
══════════════════════════════════════════ */
function HeroStat({ target, suffix, label, active }) {
  const val = useCount(target, active);
  return (
    <div>
      <div style={{ fontFamily: "'Anton',sans-serif", fontWeight: 400, fontSize: 'clamp(32px,4.5vw,54px)', lineHeight: 1, color: 'var(--ink)', }}>
        {val}{suffix}
      </div>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: 'rgba(10,10,20,0.38)', marginTop: 7, fontFamily: "'Inter', sans-serif" }}>
        {label}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   BENTO OVERVIEW CARD
══════════════════════════════════════════ */
function OverviewCard({ sector, index }) {
  const wrapRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [hov, setHov] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVisible(true); obs.disconnect(); }
    }, { threshold: 0.06 });
    if (wrapRef.current) obs.observe(wrapRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={wrapRef}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(36px)',
        transition: `opacity 580ms ${index * 90}ms cubic-bezier(.2,.8,.2,1), transform 580ms ${index * 90}ms cubic-bezier(.2,.8,.2,1)`,
      }}
    >
      <Link
        to={sector.to}
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => setHov(false)}
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 300,
          position: 'relative',
          background: hov ? `${sector.color}10` : `${sector.color}05`,
          border: `1px solid ${hov ? sector.color + '38' : sector.color + '16'}`,
          borderRadius: 24,
          padding: 'clamp(24px,2.8vw,36px)',
          overflow: 'hidden',
          textDecoration: 'none',
          cursor: 'pointer',
          transition: 'background 280ms ease, border-color 280ms ease, box-shadow 280ms ease, transform 260ms ease',
          transform: hov ? 'translateY(-5px)' : 'translateY(0)',
          boxShadow: hov ? `0 20px 48px ${sector.color}1E` : '0 2px 8px rgba(10,10,20,0.04)',
        }}
      >
        {/* Watermark number */}
        <div style={{
          position: 'absolute', bottom: -18, right: 10,
          fontFamily: "'Anton',sans-serif",
          fontWeight: 400,
          fontSize: 'clamp(80px,12vw,128px)',
          color: sector.color, opacity: hov ? 0.11 : 0.055,
          lineHeight: 1, pointerEvents: 'none', userSelect: 'none',
          transition: 'opacity 280ms',
        }}>{sector.num}</div>

        {/* Subtle dot pattern */}
        <div style={{
          position: 'absolute', inset: 0, borderRadius: 24,
          backgroundImage: `radial-gradient(${sector.color}18 1px, transparent 1px)`,
          backgroundSize: '22px 22px',
          pointerEvents: 'none', opacity: hov ? 1 : 0,
          transition: 'opacity 320ms',
        }}/>

        {/* Top row */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10, marginBottom: 20, position: 'relative', zIndex: 1 }}>
          <SectorIcon name={sector.name} color={sector.color} size={48} />
          <span style={{
            padding: '4px 11px',
            background: `${sector.color}10`,
            border: `1px solid ${sector.color}26`,
            borderRadius: 999,
            fontFamily: "'Inter', sans-serif",
            fontSize: 9, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase', color: sector.color,
            whiteSpace: 'nowrap',
          }}>
            {sector.tag}
          </span>
        </div>

        {/* Name */}
        <div style={{ flex: 1, position: 'relative', zIndex: 1 }}>
          <h3 style={{
            fontFamily: "'Anton',sans-serif",
            fontWeight: 400,
            fontSize: 'clamp(20px,2.4vw,28px)',
            lineHeight: 1.06,
            color: 'var(--ink)', margin: '0 0 4px',
          }}>
            {sector.name}
          </h3>
          <div style={{
            fontFamily: "'Instrument Serif', serif",
            fontStyle: 'italic',
            fontWeight: 400,
            fontSize: 'clamp(14px,1.6vw,18px)',
            color: sector.color, lineHeight: 1.2,
          }}>
            {sector.accent}
          </div>

          {/* Use case pills */}
          <div style={{ marginTop: 16, display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {sector.cases.slice(0, 3).map(c => (
              <span key={c} style={{
                padding: '3px 9px',
                background: `${sector.color}08`,
                border: `1px solid ${sector.color}16`,
                borderRadius: 999,
                fontSize: 11, fontWeight: 500,
                color: 'var(--ink-soft)',
              }}>
                {c}
              </span>
            ))}
          </div>
        </div>

        {/* Bottom CTA row */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginTop: 24, paddingTop: 18, borderTop: `1px solid ${sector.color}14`,
          position: 'relative', zIndex: 1,
        }}>
          <span style={{ fontSize: 12.5, fontWeight: 700,  color: sector.color }}>
            Explore Industry
          </span>
          <div style={{
            width: 34, height: 34, borderRadius: '50%',
            background: hov ? sector.color : `${sector.color}14`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'background 280ms ease, transform 260ms ease',
            transform: hov ? 'rotate(-45deg)' : 'rotate(0deg)',
            flexShrink: 0,
          }}>
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
              <path d="M2.5 6.5h8M7 2.5l4 4-4 4" stroke={hov ? '#fff' : sector.color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
        </div>
      </Link>
    </div>
  );
}

/* ══════════════════════════════════════════
   SECTOR SPOTLIGHT — rich two-column layout
══════════════════════════════════════════ */
function SectorSpotlight({ sector, index }) {
  const [ref, visible] = useRevealOnce(0.07);
  const panelLeft = index % 2 === 0;

  /* Visual (colored) panel */
  const visualPanel = (
    <div
      style={{
        position: 'relative',
        background: `linear-gradient(145deg, ${sector.color}14 0%, ${sector.color}06 100%)`,
        border: `1px solid ${sector.color}20`,
        borderRadius: 28,
        padding: 'clamp(36px,4vw,52px)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: 28,
        minHeight: 'clamp(340px,38vw,440px)',
        opacity: visible ? 1 : 0,
        transform: visible ? 'none' : `translateX(${panelLeft ? '-56px' : '56px'})`,
        transition: 'opacity 680ms cubic-bezier(.2,.8,.2,1), transform 680ms cubic-bezier(.2,.8,.2,1)',
      }}
    >
      {/* Large watermark ordinal */}
      <div style={{
        position: 'absolute', bottom: -20, right: 8,
        fontFamily: "'Anton',sans-serif",
        fontWeight: 400,
        fontSize: 'clamp(100px,16vw,172px)',
        color: sector.color, opacity: 0.065,
        lineHeight: 1, pointerEvents: 'none', userSelect: 'none',
      }}>{sector.num}</div>

      {/* Floating ambient orbs */}
      <div style={{
        position: 'absolute', top: '18%', right: '12%',
        width: 110, height: 110, borderRadius: '50%',
        background: `radial-gradient(circle, ${sector.color}20 0%, transparent 70%)`,
        pointerEvents: 'none',
        animation: 'spotOrbA 7s ease-in-out infinite',
      }}/>
      <div style={{
        position: 'absolute', bottom: '20%', left: '8%',
        width: 72, height: 72, borderRadius: '50%',
        background: `radial-gradient(circle, ${sector.color}16 0%, transparent 70%)`,
        pointerEvents: 'none',
        animation: 'spotOrbB 9s ease-in-out infinite',
      }}/>

      {/* Icon */}
      <div style={{ position: 'relative', zIndex: 1 }}>
        <SectorIcon name={sector.name} color={sector.color} size={64} />
      </div>

      {/* Sector name */}
      <div style={{ position: 'relative', zIndex: 1 }}>
        <h2 style={{
          fontFamily: "'Anton',sans-serif",
          fontWeight: 400,
          fontSize: 'clamp(30px,3.8vw,48px)',
          lineHeight: 1.06,
          color: 'var(--ink)', margin: 0,
        }}>{sector.name}</h2>
        <div style={{
          fontFamily: "'Instrument Serif', serif",
          fontStyle: 'italic',
          fontWeight: 400,
          fontSize: 'clamp(16px,2vw,24px)',
          color: sector.color, lineHeight: 1.2, marginTop: 4,
        }}>{sector.accent}</div>
      </div>

      {/* Inline stats */}
      <div style={{
        position: 'relative', zIndex: 1,
        display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10,
        paddingTop: 22, borderTop: `1px solid ${sector.color}1E`,
      }}>
        {sector.stats.map(stat => (
          <div key={stat.l}>
            <div style={{
              fontFamily: "'Anton',sans-serif",
              fontWeight: 400,
              fontSize: 'clamp(17px,2vw,23px)',
              color: sector.color, lineHeight: 1,
            }}>{stat.v}</div>
            <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: 'rgba(10,10,20,0.36)', marginTop: 4 }}>
              {stat.l}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  /* Content panel */
  const contentPanel = (
    <div
      style={{
        display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 22,
        opacity: visible ? 1 : 0,
        transform: visible ? 'none' : `translateX(${panelLeft ? '56px' : '-56px'})`,
        transition: 'opacity 680ms 120ms cubic-bezier(.2,.8,.2,1), transform 680ms 120ms cubic-bezier(.2,.8,.2,1)',
      }}
    >
      {/* Tag badge */}
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 7,
        padding: '5px 14px',
        background: `${sector.color}0C`, border: `1px solid ${sector.color}26`,
        borderRadius: 999, width: 'fit-content',
      }}>
        <span style={{ width: 5, height: 5, borderRadius: '50%', background: sector.color, flexShrink: 0 }}/>
        <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 10, letterSpacing: 2.5, textTransform: 'uppercase', color: sector.color }}>
          {sector.tag}
        </span>
      </div>

      {/* Headline */}
      <div>
        <h3 style={{
          fontFamily: "'Anton',sans-serif",
          fontWeight: 400,
          fontSize: 'clamp(24px,3vw,40px)',
          lineHeight: 1.08,
          color: 'var(--ink)', margin: 0,
        }}>{sector.headline}</h3>
        <div style={{ width: 44, height: 3, background: sector.color, borderRadius: 2, marginTop: 16, opacity: 0.7 }}/>
      </div>

      {/* Description */}
      <p style={{ fontSize: 'clamp(14px,1.4vw,15.5px)', lineHeight: 1.84, color: 'var(--ink-soft)', margin: 0, maxWidth: 480 }}>
        {sector.desc}
      </p>

      {/* Use cases — 2×3 grid */}
      <div>
        <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 9.5, letterSpacing: 2.5, textTransform: 'uppercase', color: 'rgba(10,10,20,0.38)', marginBottom: 10 }}>
          USE CASES
        </div>
        <div className="cases-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7 }}>
          {sector.cases.map((c, i) => (
            <div
              key={c}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '9px 12px',
                background: `${sector.color}07`,
                border: `1px solid ${sector.color}16`,
                borderRadius: 10,
                fontSize: 12, fontWeight: 500, color: 'var(--ink)',
                opacity: visible ? 1 : 0,
                transform: visible ? 'none' : 'scale(0.90)',
                transition: `opacity 420ms ${i * 55 + 280}ms ease, transform 420ms ${i * 55 + 280}ms ease`,
              }}
            >
              <span style={{ width: 4, height: 4, borderRadius: '50%', background: sector.color, flexShrink: 0 }}/>
              {c}
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <Link
        to={sector.to}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 10,
          padding: '12px 22px',
          background: sector.color,
          color: '#fff', borderRadius: 999,
          fontSize: 13, fontWeight: 700, 
          width: 'fit-content',
          boxShadow: `0 8px 24px ${sector.color}3E`,
          transition: 'transform 220ms ease, box-shadow 220ms ease',
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 14px 36px ${sector.color}58`; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = `0 8px 24px ${sector.color}3E`; }}
      >
        Explore {sector.name} Solutions
        <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
          <path d="M2.5 6.5h8M7 2.5l4 4-4 4" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </Link>
    </div>
  );

  return (
    <div
      ref={ref}
      className="sector-spotlight"
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1.45fr',
        gap: 'clamp(14px,2.2vw,28px)',
        marginBottom: 'clamp(20px,2.5vw,32px)',
      }}
    >
      {panelLeft ? <>{visualPanel}{contentPanel}</> : <>{contentPanel}{visualPanel}</>}
    </div>
  );
}

/* ══════════════════════════════════════════
   CTA SECTION
══════════════════════════════════════════ */
function SectorsCTA() {
  const [ref, visible] = useRevealOnce();
  return (
    <section style={{ padding: 'clamp(48px,6vw,80px) clamp(20px,5vw,48px)' }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        <div
          ref={ref}
          className="sectors-cta-inner"
          style={{
            background: 'linear-gradient(135deg, var(--ink) 0%, #1a1a2e 100%)',
            borderRadius: 28, padding: 'clamp(40px,5vw,72px)',
            display: 'flex', flexWrap: 'wrap', gap: 32,
            alignItems: 'center', justifyContent: 'space-between',
            opacity: visible ? 1 : 0,
            transform: visible ? 'none' : 'translateY(32px)',
            transition: 'opacity 580ms ease, transform 580ms ease',
          }}
        >
          <div>
            <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 10.5, letterSpacing: 3, color: 'rgba(255,255,255,0.38)', textTransform: 'uppercase', marginBottom: 12 }}>
              LET'S TALK
            </div>
            <h2 style={{ fontFamily: "'Anton',sans-serif", fontWeight: 400, fontSize: 'clamp(26px,4vw,52px)', color: '#fff', margin: 0, lineHeight: 1.08 }}>
              Your industry,{' '}
              <span style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', fontWeight: 400, color: 'var(--red)' }}>our expertise.</span>
            </h2>
            <p style={{ fontSize: 15, lineHeight: 1.8, color: 'rgba(255,255,255,0.5)', marginTop: 14, marginBottom: 0, maxWidth: 480 }}>
              Tell us your use case and we'll tell you exactly how Zone-X can solve it — with a real technical recommendation, not a brochure.
            </p>
          </div>
          <Link
            to="/contact"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 12,
              padding: 'clamp(14px,2vw,18px) clamp(28px,3.5vw,40px)',
              background: 'var(--red)', color: '#fff',
              borderRadius: 999, fontSize: 15, fontWeight: 700,
               flexShrink: 0,
              boxShadow: '0 8px 28px rgba(255,0,4,0.48)',
              transition: 'transform 200ms ease, box-shadow 200ms ease',
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 16px 40px rgba(255,0,4,0.6)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 8px 28px rgba(255,0,4,0.48)'; }}
          >
            Discuss Your Project
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2.5 7h9M8 3l4 4-4 4" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════
   PAGE
══════════════════════════════════════════ */
export default function SectorsPage() {
  const [statsRef, statsActive] = useRevealOnce(0.3);
  const [overviewRef, overviewVisible] = useRevealOnce(0.08);

  const TICKER = ['Healthcare', 'Utilities & Energy', 'Industrial IoT', 'Education'];
  const tickerText = [...TICKER, ...TICKER].join('  ·  ');

  return (
    <>
      <Helmet>
        <title>Industries We Serve — IoT Solutions by Zone-X</title>
        <meta name="description" content="Zone-X builds IoT solutions across healthcare, utilities & energy, industrial IoT, and education — engineered for the specific demands of each industry." />
        <link rel="canonical" href="https://zone-x.dev/sectors" />
      </Helmet>

      {/* ════════════════ HERO ════════════════ */}
      <section style={{ paddingTop: 120, paddingBottom: 0, position: 'relative', overflow: 'hidden' }}>
        {/* Dotted grid */}
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', backgroundImage: 'radial-gradient(rgba(10,10,20,0.032) 1px, transparent 1px)', backgroundSize: '28px 28px' }}/>
        {/* Accent blobs */}
        <div style={{ position: 'absolute', top: -120, right: '3%', width: 620, height: 620, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,0,4,0.07) 0%, transparent 58%)', pointerEvents: 'none' }}/>
        <div style={{ position: 'absolute', bottom: -80, left: '2%', width: 440, height: 440, borderRadius: '50%', background: 'radial-gradient(circle, rgba(0,60,255,0.06) 0%, transparent 65%)', pointerEvents: 'none' }}/>
        <div style={{ position: 'absolute', top: '35%', left: '42%', width: 300, height: 300, borderRadius: '50%', background: 'radial-gradient(circle, rgba(100,80,200,0.025) 0%, transparent 70%)', pointerEvents: 'none' }}/>

        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 clamp(20px,5vw,48px)', position: 'relative', zIndex: 1 }}>
          {/* 2-column hero grid */}
          <div className="sectors-hero-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'clamp(32px,4vw,56px)', alignItems: 'center' }}>

            {/* LEFT — text content */}
            <div>
              {/* Label chip */}
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '5px 14px', background: 'rgba(255,0,4,0.07)', border: '1px solid rgba(255,0,4,0.2)', borderRadius: 999, marginBottom: 32 }}>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--red)', flexShrink: 0 }}/>
                <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 10.5, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--red)' }}>
                  04 INDUSTRIES
                </span>
              </div>

              <h1 style={{
                fontFamily: "'Anton',sans-serif",
                fontWeight: 400,
                fontSize: 'clamp(48px,8vw,104px)',
                lineHeight: 0.96,
                color: 'var(--ink)', margin: 0,
              }}>
                Industries<br/>
                <span style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', fontWeight: 400, color: 'var(--red)' }}>
                  We Serve
                </span>
              </h1>

              <p style={{
                fontSize: 'clamp(14px,1.35vw,16px)', lineHeight: 1.84,
                color: 'var(--ink-soft)', maxWidth: 460,
                marginTop: 24, marginBottom: 44,
              }}>
                Zone-X engineers IoT systems for specific industries — understanding the protocols, regulations, and operational realities that make each vertical unique. Purpose-built, not generic.
              </p>

              {/* Stats row */}
              <div
                ref={statsRef}
                className="hero-stats-row"
                style={{ display: 'flex', gap: 'clamp(24px,4vw,52px)', flexWrap: 'wrap', paddingBottom: 52, borderBottom: '1px solid rgba(10,10,20,0.08)' }}
              >
                {HERO_STATS.map(s => <HeroStat key={s.label} {...s} active={statsActive} />)}
              </div>
            </div>

            {/* RIGHT — orbit visualization */}
            <div
              className="sectors-hero-viz"
              style={{
                display: 'flex', justifyContent: 'center', alignItems: 'center',
                opacity: statsActive ? 1 : 0,
                transform: statsActive ? 'scale(1) translateY(0)' : 'scale(0.88) translateY(20px)',
                transition: 'opacity 900ms 250ms cubic-bezier(.2,.8,.2,1), transform 900ms 250ms cubic-bezier(.2,.8,.2,1)',
              }}
            >
              <IndustriesOrbit/>
            </div>
          </div>
        </div>

        {/* Ticker marquee */}
        <div style={{ overflow: 'hidden', borderTop: '1px solid rgba(10,10,20,0.07)', padding: '16px 0', background: 'rgba(10,10,20,0.02)' }}>
          <div style={{ display: 'flex', animation: 'sect-ticker 26s linear infinite', whiteSpace: 'nowrap', willChange: 'transform' }}>
            <span style={{ fontFamily: "'Anton',sans-serif", fontWeight: 400, fontSize: 'clamp(12px,1.8vw,18px)', letterSpacing: '0.05em', textTransform: 'uppercase', color: 'rgba(10,10,20,0.18)', paddingRight: 40 }}>{tickerText}</span>
            <span style={{ fontFamily: "'Anton',sans-serif", fontWeight: 400, fontSize: 'clamp(12px,1.8vw,18px)', letterSpacing: '0.05em', textTransform: 'uppercase', color: 'rgba(10,10,20,0.18)', paddingRight: 40 }}>{tickerText}</span>
          </div>
        </div>
      </section>

      {/* ════════════════ OVERVIEW GRID ════════════════ */}
      <section style={{ padding: 'clamp(56px,6vw,80px) clamp(20px,5vw,48px)' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>

          {/* Section header */}
          <div
            ref={overviewRef}
            style={{
              marginBottom: 'clamp(32px,4vw,52px)',
              display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
              flexWrap: 'wrap', gap: 16,
              opacity: overviewVisible ? 1 : 0,
              transform: overviewVisible ? 'none' : 'translateY(24px)',
              transition: 'opacity 540ms ease, transform 540ms ease',
            }}
          >
            <div>
              <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 10.5, letterSpacing: 3, color: 'var(--blue)', textTransform: 'uppercase' }}>
                SECTOR OVERVIEW
              </span>
              <h2 style={{
                fontFamily: "'Anton',sans-serif",
                fontWeight: 400,
                fontSize: 'clamp(28px,4vw,52px)',
                lineHeight: 1.06,
                color: 'var(--ink)', marginTop: 10, marginBottom: 0,
              }}>
                Four industries,{' '}
                <span style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', fontWeight: 400, color: 'var(--red)' }}>
                  one engineering partner
                </span>
              </h2>
            </div>
            <p style={{ fontSize: 'clamp(13px,1.3vw,15px)', lineHeight: 1.72, color: 'var(--ink-soft)', margin: 0, maxWidth: 380 }}>
              Purpose-built IoT solutions tailored to the specific demands, regulations, and failure modes of each vertical.
            </p>
          </div>

          {/* 3-column card grid */}
          <div className="overview-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 'clamp(12px,1.4vw,18px)' }}>
            {SECTORS.map((s, i) => <OverviewCard key={s.num} sector={s} index={i} />)}
          </div>
        </div>
      </section>

      {/* ════════════════ SECTOR SPOTLIGHTS ════════════════ */}
      <section style={{ background: 'var(--paper)', padding: 'clamp(56px,6vw,80px) clamp(20px,5vw,48px) clamp(32px,4vw,48px)' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>

          {/* Section header */}
          <SectionHeader
            eyebrow="DEEP DIVE"
            headline={<>IoT that works for{' '}<em style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic', fontWeight: 400, color: 'var(--red)' }}>your industry</em></>}
            body="Generic IoT platforms are built for average use cases. Zone-X engineers systems for specific industries — understanding the protocols, regulations, failure modes, and operational realities that make each vertical unique."
          />

          {SECTORS.map((s, i) => <SectorSpotlight key={s.num} sector={s} index={i} />)}
        </div>
      </section>

      {/* ════════════════ CTA ════════════════ */}
      <SectorsCTA />

      <Footer />

      <style>{`
        @keyframes sect-ticker { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        @keyframes spotOrbA { 0%,100%{transform:translateY(0) scale(1)} 50%{transform:translateY(-14px) scale(1.06)} }
        @keyframes spotOrbB { 0%,100%{transform:translateY(0) scale(1)} 50%{transform:translateY(12px) scale(0.95)} }

        @keyframes sectOrbit {
          from { transform: rotate(0deg) translateX(168px) rotate(0deg); }
          to   { transform: rotate(360deg) translateX(168px) rotate(-360deg); }
        }
        @keyframes sectOrbitFast {
          from { transform: rotate(0deg) translateX(105px) rotate(0deg); }
          to   { transform: rotate(360deg) translateX(105px) rotate(-360deg); }
        }

        @media(max-width:900px){
          .sector-spotlight{grid-template-columns:1fr!important}
          .overview-grid{grid-template-columns:1fr 1fr!important}
          .sectors-hero-grid{grid-template-columns:1fr!important}
          .sectors-hero-viz{display:none!important}
        }
        @media(max-width:560px){
          .overview-grid{grid-template-columns:1fr!important}
          .cases-grid{grid-template-columns:1fr!important}
          .hero-stats-row{gap:24px}
        }
        @media(max-width:480px){
          .hero-stats-row{display:grid;grid-template-columns:1fr 1fr}
        }
        @media(max-width:680px){
          .sectors-cta-inner{flex-direction:column;align-items:flex-start}
        }
      `}</style>
    </>
  );
}

/* ─── shared section header ─── */
function SectionHeader({ eyebrow, headline, body }) {
  const [ref, visible] = useRevealOnce(0.1);
  return (
    <div
      ref={ref}
      style={{
        marginBottom: 'clamp(40px,5vw,64px)',
        opacity: visible ? 1 : 0,
        transform: visible ? 'none' : 'translateY(24px)',
        transition: 'opacity 540ms ease, transform 540ms ease',
      }}
    >
      <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 10.5, letterSpacing: 3, color: 'var(--blue)', textTransform: 'uppercase' }}>
        {eyebrow}
      </span>
      <h2 style={{
        fontFamily: "'Anton',sans-serif",
        fontWeight: 400,
        fontSize: 'clamp(28px,4vw,52px)',
        lineHeight: 1.06,
        color: 'var(--ink)', marginTop: 10, marginBottom: 0,
      }}>
        {headline}
      </h2>
      {body && (
        <p style={{ fontSize: 'clamp(14px,1.4vw,16px)', lineHeight: 1.82, color: 'var(--ink-soft)', marginTop: 14, marginBottom: 0, maxWidth: 600 }}>
          {body}
        </p>
      )}
    </div>
  );
}
